export const isClosed = status => ["completed", "cancelled"].includes(status);
export function needsClosure(row) {
  const stops = row.detail && row.detail.stops;
  return !isClosed(row.summary.status) && Array.isArray(stops) && stops.length > 0
    && stops.every(stop => ["completed", "skipped"].includes(stop.status));
}
const vehicleKey = value => (value || "").replace(/[^a-z0-9]/gi, "").toUpperCase();

export function defaultDriver(assignment, drivers, now = new Date()) {
  if (!assignment || assignment.driver_id || assignment.driver_display_name || !assignment.vehicle_ref) return null;
  const candidates = drivers.filter(driver => driver.active
    && vehicleKey(driver.vehicle_ref) === vehicleKey(assignment.vehicle_ref)
    && driver.assignment_role === "default_vehicle" && driver.assignment_status === "confirmed"
    && (!driver.valid_from || Date.parse(driver.valid_from) <= now.getTime())
    && (!driver.valid_to || Date.parse(driver.valid_to) > now.getTime()));
  const unique = [...new Map(candidates.map(driver => [driver.driver_id, driver])).values()];
  return unique.length === 1 ? unique[0].display_name : null;
}

export function needsReconciliation(row, now = new Date()) {
  return !isClosed(row.summary.status) && row.detail && row.detail.planned_end
    && Date.parse(row.detail.planned_end) < now.getTime();
}

const normalize = value => String(value || "").trim().toLocaleLowerCase();
export const taskVehicleKey = row => row.uncertain ? "" : vehicleKey(row.assignment && row.assignment.vehicle_ref);
export function vehicleOptions(rows, drivers, now = new Date()) {
  const groups = new Map();
  rows.forEach(row => {
    const key = taskVehicleKey(row);
    if (!key) return;
    const assignment = row.assignment;
    if (!groups.has(key)) groups.set(key, { value: key, vehicle: assignment.vehicle_ref, names: new Set() });
    // Historical tasks retain their actual assignment, never today's default driver.
    const name = assignment.driver_display_name || assignment.driver_id
      || (!isClosed(row.summary.status) && defaultDriver(assignment, drivers, now));
    if (name) groups.get(key).names.add(name);
  });
  return [...groups.values()].map(group => ({ value: group.value,
    label: `${group.vehicle}${group.names.size ? ` / ${[...group.names].sort().join(", ")}` : ""}`,
  })).sort((a, b) => a.label.localeCompare(b.label));
}
export const customerKey = row => normalize(row.summary.customer_ref || (row.detail && row.detail.customer_name_snapshot));
export function customerOptions(rows) {
  return [...new Map(rows.map(row => [customerKey(row), { value: customerKey(row), label: row.summary.customer_ref || (row.detail && row.detail.customer_name_snapshot) }])).values()]
    .filter(option => option.value).sort((a, b) => a.label.localeCompare(b.label));
}
export function requiresAttention(row) {
  return !!row.uncertain || (!isClosed(row.summary.status) && (row.summary.status === "blocked"
    || !!(row.exceptions && row.exceptions.length) || needsClosure(row) || needsReconciliation(row) || row.deadlineState === "overdue"));
}
export function filterTaskRows(rows, status, customer, search = "", attention = false, lifecycle = "all", vehicle = "") {
  const terms = normalize(search).split(/\s+/).filter(Boolean);
  return rows.filter(row => {
    const state = row.summary.status;
    const match = status === "all" || (status === "open" ? ["planned", "active", "blocked"].includes(state)
      : status === "closed" ? isClosed(state)
      : status === "reconcile" ? needsReconciliation(row) : status === "closure" ? needsClosure(row) : state === status);
    const assignment = row.assignment || {};
    const detail = row.detail || {};
    const searchable = normalize([row.summary.order_ref, row.summary.title, row.summary.customer_ref,
      detail.customer_name_snapshot, assignment.vehicle_ref, assignment.trailer_ref, assignment.equipment_ref,
      assignment.driver_display_name, ...(detail.stops || []).map(stop => stop.location_name)].filter(Boolean).join(" "));
    return match && (!customer || customerKey(row) === normalize(customer)) && terms.every(term => searchable.includes(term))
      && (!attention || requiresAttention(row)) && (lifecycle === "all" || state === lifecycle)
      && (!vehicle || taskVehicleKey(row) === vehicleKey(vehicle));
  });
}
